import {
  UIElementType,
  UIElementInstance,
  UIElementServiceInterface,
  UIElementDataProvider,
  UIElementRendererProvider,
  SupabaseUIElementType,
  SupabaseUIElementInstance
} from './types';
import { DEFAULT_UI_ELEMENT_TYPES } from './config/uiElementTypeDefaults';

import type { WriteScope } from '@/modules/undoRedo';
const DEFAULT_DURATION = 10;

function generateUUID(): string {
  return crypto.randomUUID();
}

export function convertToUIElementType(row: SupabaseUIElementType): UIElementType {
  return {
    typeId: row.type_id,
    sourceName: row.source_name,
    category: row.category,
    displayName: row.display_name,
    textBody: row.text_body,
    maxCharacterCount: row.max_character_count,
    styleCornerRadius: row.style_corner_radius,
    styleBackgroundColor: row.style_background_color,
    styleFont: row.style_font,
    styleTextSize: row.style_text_size,
    styleTextColour: row.style_text_colour,
    styleBorder: row.style_border,
    styleBorderRadius: row.style_border_radius,
    styleBorderThickness: row.style_border_thickness,
    styleBorderColour: row.style_border_colour,
    styleAlignment: row.style_alignment,
    styleJustify: row.style_justify,
    orgMemberId: row.org_member_id,
    availability: row.availability,
    isActive: row.is_active,
    uss: row.uss,
  };
}

export function convertToUIElementInstance(row: SupabaseUIElementInstance): UIElementInstance {
  const beginTime = row.begin_time ?? 0;
  const endTime = row.end_time ?? beginTime + DEFAULT_DURATION;
  return {
    instanceId: row.instance_id,
    sourceName: row.source_name,
    category: row.category,
    displayName: row.display_name,
    textBody: row.text_body,
    maxCharacterCount: row.max_character_count,
    styleCornerRadius: row.style_corner_radius,
    styleBackgroundColor: row.style_background_color,
    styleFont: row.style_font,
    styleTextSize: row.style_text_size,
    styleTextColour: row.style_text_colour,
    styleBorder: row.style_border,
    styleBorderRadius: row.style_border_radius,
    styleBorderThickness: row.style_border_thickness,
    styleBorderColour: row.style_border_colour,
    styleAlignment: row.style_alignment,
    styleJustify: row.style_justify,
    uiElementTypeId: row.ui_element_type_id,
    orgMemberId: row.org_member_id,
    sceneInstanceId: row.scene_instance_id,
    availability: row.availability,
    isActive: row.is_active,
    uss: row.uss,
    beginTime,
    endTime,
    displayOrder: null,
    isLocked: false,
    isVisible: true,
  };
}

function createInstanceRowFromType(
  type: UIElementType,
  sceneInstanceId: string,
  orgMemberId: number,
  beginTime: number,
  endTime: number
): SupabaseUIElementInstance {
  return {
    instance_id: generateUUID(),
    source_name: type.sourceName,
    category: type.category,
    display_name: type.displayName,
    text_body: type.textBody,
    max_character_count: type.maxCharacterCount,
    style_corner_radius: type.styleCornerRadius,
    style_background_color: type.styleBackgroundColor,
    style_font: type.styleFont,
    style_text_size: type.styleTextSize,
    style_text_colour: type.styleTextColour,
    style_border: type.styleBorder,
    style_border_radius: type.styleBorderRadius,
    style_border_thickness: type.styleBorderThickness,
    style_border_colour: type.styleBorderColour,
    style_alignment: type.styleAlignment,
    style_justify: type.styleJustify,
    ui_element_type_id: type.typeId,
    org_member_id: orgMemberId,
    scene_instance_id: sceneInstanceId,
    availability: 'private',
    is_active: true,
    uss: type.uss,
    begin_time: beginTime,
    end_time: endTime,
  };
}

function duplicateInstanceRow(
  source: UIElementInstance,
  orgMemberId: number,
  beginTime: number,
  displayName: string
): SupabaseUIElementInstance {
  return {
    instance_id: generateUUID(),
    source_name: source.sourceName,
    category: source.category,
    display_name: displayName,
    text_body: source.textBody,
    max_character_count: source.maxCharacterCount,
    style_corner_radius: source.styleCornerRadius,
    style_background_color: source.styleBackgroundColor,
    style_font: source.styleFont,
    style_text_size: source.styleTextSize,
    style_text_colour: source.styleTextColour,
    style_border: source.styleBorder,
    style_border_radius: source.styleBorderRadius,
    style_border_thickness: source.styleBorderThickness,
    style_border_colour: source.styleBorderColour,
    style_alignment: source.styleAlignment,
    style_justify: source.styleJustify,
    ui_element_type_id: source.uiElementTypeId,
    org_member_id: orgMemberId,
    scene_instance_id: source.sceneInstanceId,
    availability: source.availability,
    is_active: source.isActive,
    uss: source.uss,
    begin_time: beginTime,
    end_time: beginTime + (source.endTime - source.beginTime),
  };
}

function instanceToTypeRow(
  instance: UIElementInstance,
  orgMemberId: number,
  typeId?: string
): SupabaseUIElementType {
  return {
    type_id: typeId ?? generateUUID(),
    source_name: instance.sourceName,
    category: instance.category,
    display_name: instance.displayName,
    text_body: instance.textBody,
    max_character_count: instance.maxCharacterCount,
    style_corner_radius: instance.styleCornerRadius,
    style_background_color: instance.styleBackgroundColor,
    style_font: instance.styleFont,
    style_text_size: instance.styleTextSize,
    style_text_colour: instance.styleTextColour,
    style_border: instance.styleBorder,
    style_border_radius: instance.styleBorderRadius,
    style_border_thickness: instance.styleBorderThickness,
    style_border_colour: instance.styleBorderColour,
    style_alignment: instance.styleAlignment,
    style_justify: instance.styleJustify,
    org_member_id: orgMemberId,
    availability: 'private',
    is_active: true,
    uss: instance.uss,
  };
}

export class UIElementService implements UIElementServiceInterface {
  constructor(
    private readonly dataProvider: UIElementDataProvider,
    private readonly renderer: UIElementRendererProvider
  ) {}

  async getAvailableTypes(orgMemberId: number): Promise<UIElementType[]> {
    const userTypeRows = await this.dataProvider.fetchTypesByOrgMember(orgMemberId);
    const userTypes = userTypeRows.map(convertToUIElementType);
    return [...DEFAULT_UI_ELEMENT_TYPES, ...userTypes];
  }

  async addToScene(
    typeId: string,
    sceneInstanceId: string,
    userId: string,
    beginTime: number,
    scope?: WriteScope
  ): Promise<UIElementInstance> {
    const orgMemberId = await this.dataProvider.fetchOrgMemberIdByUserId(userId);
    if (orgMemberId === null) {
      throw new Error(`Could not resolve org member for user: ${userId}`);
    }

    const type = await this.resolveType(typeId, orgMemberId);
    if (!type) {
      throw new Error(`UI element type not found: ${typeId}`);
    }

    // Create instance from type only; never create/upsert a type here (library must show types from DB)
    const endTime = beginTime + DEFAULT_DURATION;
    const instanceRow = createInstanceRowFromType(type, sceneInstanceId, orgMemberId, beginTime, endTime);
    const savedRow = await this.dataProvider.addInstance(instanceRow, scope);

    this.renderer.uiElementAdded(savedRow);

    return convertToUIElementInstance(savedRow);
  }

  async updateInstance(
    instanceId: string,
    updates: Partial<UIElementInstance>,
    scope?: WriteScope
  ): Promise<void> {
    const dbUpdates: Partial<SupabaseUIElementInstance> = {};
    if (updates.textBody !== undefined) dbUpdates.text_body = updates.textBody;
    if (updates.displayName !== undefined) dbUpdates.display_name = updates.displayName;
    if (updates.uiElementTypeId !== undefined) dbUpdates.ui_element_type_id = updates.uiElementTypeId;
    if (updates.styleBackgroundColor !== undefined) dbUpdates.style_background_color = updates.styleBackgroundColor;
    if (updates.styleTextColour !== undefined) dbUpdates.style_text_colour = updates.styleTextColour;
    if (updates.styleFont !== undefined) dbUpdates.style_font = updates.styleFont;
    if (updates.styleTextSize !== undefined) dbUpdates.style_text_size = updates.styleTextSize;
    if (updates.styleCornerRadius !== undefined) dbUpdates.style_corner_radius = updates.styleCornerRadius;
    if (updates.styleBorder !== undefined) dbUpdates.style_border = updates.styleBorder;
    if (updates.styleBorderRadius !== undefined) dbUpdates.style_border_radius = updates.styleBorderRadius;
    if (updates.styleBorderThickness !== undefined) dbUpdates.style_border_thickness = updates.styleBorderThickness;
    if (updates.styleBorderColour !== undefined) dbUpdates.style_border_colour = updates.styleBorderColour;
    if (updates.styleAlignment !== undefined) dbUpdates.style_alignment = updates.styleAlignment;
    if (updates.styleJustify !== undefined) dbUpdates.style_justify = updates.styleJustify;
    if (updates.isActive !== undefined) dbUpdates.is_active = updates.isActive;
    if (updates.uss !== undefined) dbUpdates.uss = updates.uss;
    if (updates.beginTime !== undefined) dbUpdates.begin_time = updates.beginTime;
    if (updates.endTime !== undefined) dbUpdates.end_time = updates.endTime;

    const updatedRow = await this.dataProvider.updateInstance(instanceId, dbUpdates, scope);
    this.renderer.uiElementUpdated(updatedRow);
  }

  async deleteInstance(
    instanceId: string,
    scope?: WriteScope
  ): Promise<void> {
    const deletion = await this.dataProvider.deleteInstance(instanceId, scope);
    await this.renderer.uiElementDeleted(instanceId, deletion);
  }

  async duplicateInstance(
    source: UIElementInstance,
    userId: string,
    beginTime: number,
    displayName: string,
    scope?: WriteScope
  ): Promise<UIElementInstance> {
    const orgMemberId = await this.dataProvider.fetchOrgMemberIdByUserId(userId);
    if (orgMemberId === null) {
      throw new Error(`Could not resolve org member for user: ${userId}`);
    }
    const savedRow = await this.dataProvider.addInstance(duplicateInstanceRow(source, orgMemberId, beginTime, displayName), scope);
    this.renderer.uiElementAdded(savedRow);
    return convertToUIElementInstance(savedRow);
  }

  async saveAsType(
    instance: UIElementInstance,
    orgMemberId: number
  ): Promise<UIElementType> {
    const typeRow = instanceToTypeRow(instance, orgMemberId);
    const savedRow = await this.dataProvider.saveType(typeRow);
    return convertToUIElementType(savedRow);
  }

  async updateTypeFromInstance(
    instance: UIElementInstance,
    orgMemberId: number,
    scope?: WriteScope
  ): Promise<void> {
    const existing = await this.dataProvider.fetchTypeByTypeId(instance.uiElementTypeId);
    if (!existing || existing.org_member_id !== orgMemberId) {
      throw new Error('Cannot update this UI element type.');
    }
    const typeRow = instanceToTypeRow(instance, orgMemberId, instance.uiElementTypeId);
    await this.dataProvider.upsertType(typeRow, scope);
  }

  async resetToTypeDefaults(
    instanceId: string,
    typeId: string,
    scope?: WriteScope
  ): Promise<Partial<UIElementInstance>> {
    const type = await this.resolveType(typeId, null);
    if (!type) {
      throw new Error(`UI element type not found for reset: ${typeId}`);
    }

    const resetUpdates: Partial<SupabaseUIElementInstance> = {
      text_body: type.textBody,
      style_corner_radius: type.styleCornerRadius,
      style_background_color: type.styleBackgroundColor,
      style_font: type.styleFont,
      style_text_size: type.styleTextSize,
      style_text_colour: type.styleTextColour,
      style_border: type.styleBorder,
      style_border_radius: type.styleBorderRadius,
      style_border_thickness: type.styleBorderThickness,
      style_border_colour: type.styleBorderColour,
      style_alignment: type.styleAlignment,
      style_justify: type.styleJustify,
      uss: type.uss,
    };

    const updatedRow = await this.dataProvider.updateInstance(instanceId, resetUpdates, scope);
    this.renderer.uiElementUpdated(updatedRow);
    // Only what the reset wrote: a timeline move saved while it ran is not put back
    const saved = convertToUIElementInstance(updatedRow);
    return {
      textBody: saved.textBody,
      styleCornerRadius: saved.styleCornerRadius,
      styleBackgroundColor: saved.styleBackgroundColor,
      styleFont: saved.styleFont,
      styleTextSize: saved.styleTextSize,
      styleTextColour: saved.styleTextColour,
      styleBorder: saved.styleBorder,
      styleBorderRadius: saved.styleBorderRadius,
      styleBorderThickness: saved.styleBorderThickness,
      styleBorderColour: saved.styleBorderColour,
      styleAlignment: saved.styleAlignment,
      styleJustify: saved.styleJustify,
      uss: saved.uss,
    };
  }

  async fetchInstancesByScene(sceneInstanceId: string): Promise<UIElementInstance[]> {
    const rows = await this.dataProvider.fetchInstancesByScene(sceneInstanceId);
    return rows.map(row => convertToUIElementInstance(row));
  }

  async getType(typeId: string): Promise<UIElementType | null> {
    return this.resolveType(typeId, null);
  }

  /** Resolve type from DB first so instances are created from the template row; fall back to local default only if type is not in DB. */
  private async resolveType(typeId: string, _orgMemberId: number | null): Promise<UIElementType | null> {
    const row = await this.dataProvider.fetchTypeByTypeId(typeId);
    if (row) return convertToUIElementType(row);

    const defaultType = DEFAULT_UI_ELEMENT_TYPES.find(t => t.typeId === typeId);
    return defaultType ?? null;
  }
}
